/**
 * UYAP "insan doğrulaması" engeli (DEV-3572): tanıyıcı, kilit, kodlu hatalar, görev bayrağı, host kancaları.
 *
 * Saf tutulur: "@/main", store ve DOM'a bağlı DEĞİL, birim testten doğrudan geçer (OturumKimligi ile aynı ilke).
 * UYAP'a istek göndermeyi KESMEK içindir; engeli aşmaya çalışan hiçbir şey içermez.
 */

/** UYAP'ın CEVABINDA engel metni görüldü. Hata mesajı UYAP'ın özgün metnidir. */
export const UYAP_ENGEL_KODU = 'UYAP_ENGEL';
/** Kilit sürerken istek YEREL olarak reddedildi (UYAP'a hiç gitmedi). */
export const UYAP_ENGEL_KAPISI_KODU = 'UYAP_ENGEL_KAPISI';
export type UyapEngelHataKodu = 'UYAP_ENGEL' | 'UYAP_ENGEL_KAPISI';

export const UYAP_ENGEL_KAPISI_MESAJI = 'UYAP engeli sürüyor; istek gönderilmedi.';
/** istekOlcumu.kisitKodlari'na yazılan SABİT etiket (errorCode bilinmediği için). */
export const UYAP_ENGEL_OLCUM_ETIKETI = 'UYAP_ENGEL';

export type UyapEngelBicimi = 'json' | 'xml' | 'html' | 'metin';
/** 'gorev': iş isteğinde tanındı. 'yoklama': oturum yoklamasında (KullaniciBilgileri). 'sunucu': next-task Waiting + neden 'uyap_engel'. */
export type UyapEngelKaynagi = 'gorev' | 'yoklama' | 'sunucu';

export interface UyapEngelTanima {
    /** UYAP'ın özgün metni (yalnız aşırı uzunsa kırpılır). */
    metin: string;
    bicim: UyapEngelBicimi;
    /** Gövdede errorCode varsa (bilinmiyor; ölçüm için taşınır). */
    errorCode?: string;
}

/** Kilidi kurmak için gereken bilgi. */
export interface UyapEngelGirdisi {
    /** UYAP'ın özgün metni. Sunucu freninde (kaynak 'sunucu') boş olabilir. */
    metin: string;
    /** İlk tanıma anı (Date.now()). */
    zaman: number;
    /** Tanınan uç (UyapRequest.metricName); sunucu freninde 'next-task'. */
    uc: string;
    bicim: UyapEngelBicimi;
    kaynak: UyapEngelKaynagi;
    errorCode?: string;
    /** Engel HTTP hata (4xx/5xx) gövdesinde mi geldi. */
    httpHata?: boolean;
}

/** Kancaya verilen ve okunan bilgi. `mesaj` = `metin` (imzacı tasarımındaki alan adı; yeni kod `metin` kullanır). */
export interface UyapEngelBilgisi extends UyapEngelGirdisi {
    mesaj: string;
}

/** Tanıma yalnız iki bilinen metinle sınırlı; "İ" harfi büyük/küçük çevriminde bozulduğu için ilk harf dışarıda. */
const ENGEL_DESENLERI: RegExp[] = [
    /nsan oldugunuzu dogrula/,
    /nsan kullanimina uygun olmayan/
];

/** Bu uzunluğun üstündeki gövdeler (liste cevapları) taranmaz; engel cevapları kısadır. */
const TARAMA_SINIRI = 65536;
const DUZ_METIN_SINIRI = 4000;
const METIN_SINIRI = 2000;
const PENCERE = 500;

/** Türkçe i/ı/İ/I ve ğ ü ş ö ç ASCII'ye katlanır; büyük/küçük harf ve ASCII'leşmiş yazım fark etmez. */
function katla(metin: string): string {
    let s = String(metin);
    try {
        s = s.normalize('NFD');
    } catch (e) {
        // normalize yoksa katlama eksik kalır; eşleşme yine büyük ölçüde çalışır
    }
    return s.replace(/[\u0300-\u036f]/g, '')
        .replace(/ı/g, 'i')
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

export function uyapEngelMetniMi(metin: any): boolean {
    if (typeof metin !== 'string' || metin === '') {
        return false;
    }
    const katli = katla(metin);
    return ENGEL_DESENLERI.some((desen) => desen.test(katli));
}

function kodNoktasi(sayi: number): string {
    return isFinite(sayi) && sayi > 0 && sayi <= 0x10ffff ? String.fromCodePoint(sayi) : '';
}

function varliklariCoz(s: string): string {
    return s.replace(/&#(\d+);/g, (_t: string, n: string) => kodNoktasi(Number(n)))
        .replace(/&#x([0-9a-f]+);/gi, (_t: string, h: string) => kodNoktasi(parseInt(h, 16)))
        .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
        .replace(/&apos;/g, '\'').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
}

function etiketleriAt(s: string): string {
    return s.replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Metin sınırı aşarsa, engel cümlesi pencerede kalacak şekilde kırpar. */
function kirp(metin: string, sinir: number): string {
    if (metin.length <= sinir) {
        return metin;
    }
    const katli = katla(metin);
    let indeks = 0;
    for (const desen of ENGEL_DESENLERI) {
        const bulunan = katli.search(desen);
        if (bulunan >= 0) {
            indeks = bulunan;
            break;
        }
    }
    const bas = Math.max(0, indeks - 200);
    return metin.slice(bas, bas + sinir);
}

/** Yalnız UYAP'ın hata alanları. Belge açıklaması gibi serbest metin alanları (aciklama, mesaj...) bilerek dışarıda: yanlış pozitif botu kilitler. */
const NESNE_ALANLARI = ['error', 'message', 'errorMessage'];

function nesneyiTanimla(govde: any): UyapEngelTanima | null {
    if (govde === null || typeof govde !== 'object' || Array.isArray(govde)) {
        return null;
    }
    for (const alan of NESNE_ALANLARI) {
        const deger = govde[alan];
        if (typeof deger === 'string' && uyapEngelMetniMi(deger)) {
            const tanima: UyapEngelTanima = {metin: kirp(deger, METIN_SINIRI), bicim: 'json'};
            if (typeof govde.errorCode === 'string' && govde.errorCode !== '') {
                tanima.errorCode = govde.errorCode;
            }
            return tanima;
        }
    }
    return null;
}

function metniTanimla(ham: string): UyapEngelTanima | null {
    if (ham.length === 0 || ham.length > TARAMA_SINIRI) {
        return null;
    }
    // JSON önce, HAM metin üzerinde ayrıştırılır: kaçışlı yazım (\u011f) ve değer içindeki işaretler JSON.parse ile
    // çözülür, UYAP'ın alanı değişmeden okunur. Geçerli JSON ise karar yalnız hata alanlarına (nesneyiTanimla) bağlıdır.
    const ilkHam = ham.replace(/^\s+/, '').charAt(0);
    if (ilkHam === '{' || ilkHam === '[') {
        try {
            return nesneyiTanimla(JSON.parse(ham));
        } catch (e) {
            // geçerli JSON değil: aşağıda düz metin/HTML kuralları uygulanır
        }
    }
    // Ucuz ön eleme: iki engel metninin ikisi de "nsan" içerir (İ/i/I ve ASCII yazım fark etmez).
    if (!/nsan/i.test(ham)) {
        return null;
    }
    const s = varliklariCoz(ham);
    if (!uyapEngelMetniMi(s)) {
        return null;
    }
    const bas = s.replace(/^\s+/, '');
    const ilk = bas.charAt(0);
    if (ilk === '<') {
        // XML: yalnız <error>/<message> içindeki metin sayılır; belge metni taşıyan XML tanınmaz.
        const xml = /<(?:error|message)[^>]*>([\s\S]*?)<\/(?:error|message)>/i.exec(bas);
        if (xml && uyapEngelMetniMi(xml[1])) {
            return {metin: kirp(etiketleriAt(xml[1]), METIN_SINIRI), bicim: 'xml'};
        }
        if (/<html|<!doctype|<body/i.test(bas)) {
            return {metin: kirp(etiketleriAt(bas), PENCERE), bicim: 'html'};
        }
        return null;
    }
    // Düz metin: engel cevapları kısadır; uzun gövde belge metni olabilir, tanınmaz.
    return bas.length <= DUZ_METIN_SINIRI ? {metin: kirp(bas, METIN_SINIRI), bicim: 'metin'} : null;
}

/**
 * Cevap (ya da HTTP hata) gövdesi UYAP'ın engel metnini taşıyor mu? Taşıyorsa metin ve biçim, yoksa null.
 * Gövde string (ham JSON/XML/HTML) ya da ayrıştırılmış nesne olabilir; dizi ve Blob taranmaz (Blob önce
 * `uyapEngelBlobMetni` ile metne çevrilir, bkz. aşağısı).
 */
export function uyapEngelTanimla(govde: any): UyapEngelTanima | null {
    if (typeof govde === 'string') {
        return metniTanimla(govde);
    }
    return nesneyiTanimla(govde);
}

// ---------------------------------------------------------------- blob cevaplar (§13.5)

/** Blob olarak istenen uçlarda (EvrakIndir, MakbuzIndir vb.) bu boyutun üstündeki cevaplar OKUNMAZ: gerçek dosyadır. */
export const UYAP_ENGEL_BLOB_SINIRI = 65536;
/** Türü belirsiz blob'un içeriğine bakılırken okunan ilk bayt sayısı. */
const BLOB_ILK_BAYT = 256;
/** Türü bu listedeyse içeriğin ilk baytlarına bakılır; boş tür de belirsizdir. */
const BLOB_BELIRSIZ_TURLER = [
    'application/octet-stream', 'binary/octet-stream', 'application/x-download', 'application/download',
    'application/force-download', 'application/unknown'
];

/** Blob benzeri mi (gerçek Blob/File ya da `size` + `slice` taşıyan nesne). */
export function uyapEngelBlobMu(veri: any): boolean {
    return veri !== null && typeof veri === 'object'
        && typeof veri.size === 'number' && typeof veri.slice === 'function';
}

/** Blob.text() (Chromium/Electron) ya da yoksa FileReader (jsdom). Hata olursa ''. */
function blobuOku(blob: any): Promise<string> {
    if (typeof blob.text === 'function') {
        return Promise.resolve(blob.text()).then((metin: any) => typeof metin === 'string' ? metin : '');
    }
    return new Promise<string>((resolve) => {
        try {
            const okuyucu = new FileReader();
            okuyucu.onload = () => resolve(typeof okuyucu.result === 'string' ? okuyucu.result : '');
            okuyucu.onerror = () => resolve('');
            okuyucu.readAsText(blob);
        } catch (e) {
            resolve('');
        }
    });
}

/** Açık metin türleri: text/*, application/json, application/xml, +json/+xml (docx/xlsx gibi `openxmlformats` EŞLEŞMEZ). */
function blobMetinTuruMu(tur: string): boolean {
    return /^text\//.test(tur)
        || /^application\/(?:json|xml|xhtml\+xml)$/.test(tur)
        || /\+(?:json|xml)$/.test(tur);
}

/** İlk baytlar JSON/XML/HTML/düz metin gibi mi (ikili imza ya da kontrol karakteri yok). */
function blobBasiMetinMi(bas: string): boolean {
    // Kesilen çok baytlı karakter sonda tek bir U+FFFD üretebilir: tolere edilir.
    const s = bas.replace(/^\uFEFF/, '').replace(/\uFFFD$/, '');
    if (s.trim() === '' || /^(?:%PDF|PK)/.test(s)) {
        return false;
    }
    for (let i = 0; i < s.length; i++) {
        const kod = s.charCodeAt(i);
        // Sekme, satır sonu ve satır başı dışındaki kontrol karakterleri ve U+FFFD: ikili içerik.
        if ((kod < 32 && kod !== 9 && kod !== 10 && kod !== 13) || kod === 0xfffd) {
            return false;
        }
    }
    return true;
}

/**
 * Blob cevabı (responseType 'blob': EvrakIndir, MakbuzIndir, DosyaHesapBilgileri, MtsTakipTalebiIndir,
 * IcraTakipIlamDosyasiUygunMu, IcraTalepEvrakHazirla) engel tanıyıcısı için metne çevirir. YALNIZ küçük
 * (<= UYAP_ENGEL_BLOB_SINIRI bayt) ve JSON/XML/HTML/düz metin olan blob'lar okunur: içerik türü metin türüyse
 * doğrudan; tür boş ya da belirsiz (octet-stream) ise ilk baytlara bakılır. PDF, ZIP/UDF, resim ve diğer
 * türler ile büyük blob'lar OKUNMAZ ('' döner, davranış değişmez). Blob olmayan değer için '' döner; hata olursa ''.
 */
export async function uyapEngelBlobMetni(veri: any): Promise<string> {
    try {
        if (!uyapEngelBlobMu(veri) || veri.size <= 0 || veri.size > UYAP_ENGEL_BLOB_SINIRI) {
            return '';
        }
        const tur = String(veri.type || '').split(';')[0].trim().toLowerCase();
        if (!blobMetinTuruMu(tur)) {
            if (tur !== '' && BLOB_BELIRSIZ_TURLER.indexOf(tur) < 0) {
                return '';
            }
            const bas = await blobuOku(veri.slice(0, BLOB_ILK_BAYT));
            if (!blobBasiMetinMi(bas)) {
                return '';
            }
        }
        return await blobuOku(veri);
    } catch (e) {
        return '';
    }
}

// ---------------------------------------------------------------- kodlu hatalar

/** UYAP cevabından tanınan engel: message UYAP'ın özgün metni, kod 'UYAP_ENGEL'. */
export function uyapEngelHatasi(metin: string, errorCode?: string): Error {
    const hata: any = new Error(metin);
    hata.kod = UYAP_ENGEL_KODU;
    if (errorCode) {
        hata.errorCode = errorCode;
    }
    return hata;
}

/** Kilit sürerken yerel ret: kod 'UYAP_ENGEL_KAPISI', istek UYAP'a gitmedi. */
export function uyapEngelKapisiHatasi(): Error {
    const hata: any = new Error(UYAP_ENGEL_KAPISI_MESAJI);
    hata.kod = UYAP_ENGEL_KAPISI_KODU;
    return hata;
}

export function uyapEngelHatasiMi(hata: any): boolean {
    return !!hata && (hata.kod === UYAP_ENGEL_KODU || hata.kod === UYAP_ENGEL_KAPISI_KODU);
}

// ---------------------------------------------------------------- kilit (süreç başına, bellekte)

/** Açık kaldırmanın kaynağı (kaldırıldı kancasına gider). */
export type UyapEngelKaldirmaKaynagi = 'baslat' | 'izin' | 'panel';

let kilit: UyapEngelBilgisi | null = null;
let kuruldu: ((bilgi: UyapEngelBilgisi) => void) | null = null;
let kaldirildi: ((kaynak: UyapEngelKaldirmaKaynagi) => void) | null = null;
let disKaynak: (() => boolean) | null = null;

function bilgiOlustur(girdi: UyapEngelGirdisi): UyapEngelBilgisi {
    // Metin en çok METIN_SINIRI karakter (tanıyıcı zaten kırpar; savunma: host/sunucu kaynaklı girdi). Engel cümlesi pencerede kalır.
    const metin = typeof girdi.metin === 'string' ? kirp(girdi.metin, METIN_SINIRI) : '';
    return {...girdi, metin: metin, mesaj: metin};
}

/**
 * Kilit yoksa kurar ve true döner; kanca YALNIZ bu geçişte bir kez çağrılır. Kilit varsa false (ilk bilgi korunur).
 * Kanca KAYITSIZSA (eski host) false döner ve kilit KURULMAZ: kimsenin kaldıramayacağı kilit olmaz, eski davranış sürer.
 */
export function uyapEngelKilidiKur(girdi: UyapEngelGirdisi): boolean {
    if (kuruldu === null || kilit !== null) {
        return false;
    }
    kilit = bilgiOlustur(girdi);
    try {
        kuruldu({...kilit});
    } catch (e) {
        console.warn('UYAP engeli uyarısı gösterilemedi:', e);
    }
    return true;
}

/** Bu sürecin kendi kilidi (UYAP kapısı, döngüler, host kapıları). */
export function uyapEngelKilidiAktifMi(): boolean {
    return kilit !== null;
}

export function uyapEngelBilgisiniAl(): UyapEngelBilgisi | null {
    return kilit === null ? null : {...kilit};
}

/** Panel için sade görünüm: kilit varken `{metin, zaman}`, yoksa null. */
export function uyapEngelDurumu(): { metin: string, zaman: number } | null {
    return kilit === null ? null : {metin: kilit.metin, zaman: kilit.zaman};
}

/**
 * Döngü/sihirbaz "dur mu?" kararı için: hata kodlu bir engel hatası YA DA kilit sürüyor. Yürütücüler ve
 * DosyaBorclu gibi sarmalayıcılar hatayı `new Error(e.message)` ile kodsuz sardığı için `catch`'te yalnız
 * koda güvenilmez; kilit durumu güvenilir yoldur.
 */
export function uyapEngelDurdurmaliMi(hata?: any): boolean {
    return uyapEngelHatasiMi(hata) || kilit !== null;
}

/**
 * Pencere yenilemesinden (sessionStorage) kilidi SESSİZCE geri yükler: kanca çağrılmaz (bildirim/ses tekrarlanmaz),
 * bot durdurulmaz (host 'dur' açar). `ham.metin` ya da `ham.mesaj` okunur; boş metin yalnız kaynak 'sunucu' ise geçerli.
 * Kanca kayıtsızsa, şekil bozuksa ya da kilit zaten varsa false.
 */
export function uyapEngelKilidiniGeriYukle(ham: any): boolean {
    if (kuruldu === null || kilit !== null || !ham || typeof ham !== 'object') {
        return false;
    }
    const metin = typeof ham.metin === 'string' ? ham.metin : (typeof ham.mesaj === 'string' ? ham.mesaj : null);
    const kaynak: UyapEngelKaynagi = ham.kaynak === 'yoklama' || ham.kaynak === 'sunucu' ? ham.kaynak : 'gorev';
    if (metin === null || (metin === '' && kaynak !== 'sunucu')) {
        return false;
    }
    if (typeof ham.zaman !== 'number' || !isFinite(ham.zaman) || ham.zaman <= 0) {
        return false;
    }
    kilit = bilgiOlustur({
        metin: metin,
        zaman: ham.zaman,
        uc: typeof ham.uc === 'string' ? ham.uc : 'bilinmiyor',
        bicim: ham.bicim === 'json' || ham.bicim === 'xml' || ham.bicim === 'html' ? ham.bicim : 'metin',
        kaynak: kaynak
    });
    return true;
}

/**
 * AÇIK KALDIRMA. YALNIZ kullanıcı eylemi yolu çağırır (onaylı Başlat, "Yeniden denemeye izin ver"). Plugin hiçbir
 * koşulda kendiliğinden kaldırmaz (oto geçişi, zamanlayıcı, kimlik/büro değişimi kaldırmaz). Sunucudaki fren
 * (DELETE /api/v1/uyap-engel) HOST'un işidir; bu işlev yalnız yerel kilidi kaldırır. Kilit yoksa false.
 */
export function uyapEngelKilidiniKaldir(kaynak: UyapEngelKaldirmaKaynagi = 'izin'): boolean {
    if (kilit === null) {
        return false;
    }
    kilit = null;
    if (kaldirildi) {
        try {
            kaldirildi(kaynak);
        } catch (e) {
            console.warn('UYAP engeli kaldırma bildirimi iletilemedi:', e);
        }
    }
    return true;
}

/**
 * Dış kaynak (panel): "imzacıda engel biliniyor mu". Bu süreçte oto yayınlayacak plugin noktaları (autoPlayer, açılış
 * autoPlay) kendi kilidi yoksa bile bununla da durur. Kapı ve döngüler YALNIZ kendi kilidine bakar.
 */
export function uyapEngelDisKaynaginiAyarla(fn: (() => boolean) | null): void {
    disKaynak = fn;
}

/** 'oto' yayınlayan plugin noktaları için: kendi kilit VEYA dış kaynak. Dış kaynak fırlatırsa yok sayılır. */
export function uyapEngelOtoYayiniEngelliMi(): boolean {
    if (kilit !== null) {
        return true;
    }
    if (disKaynak === null) {
        return false;
    }
    try {
        return disKaynak() === true;
    } catch (e) {
        return false;
    }
}

// ---------------------------------------------------------------- görev bayrağı (kimlik kapısı bayrağıyla aynı desen)

export interface UyapEngelBayragi {
    kod: UyapEngelHataKodu;
    /** Dosyanın açıklamasına yazılacak UYAP özgün metni. */
    metin: string;
}

let bayrak: UyapEngelBayragi | null = null;

/** Bu görevde ilk engel olayını kaydeder (sonrakiler ezmez). Yürütücüler hatayı sarsa/yutsa da TaskHandler okur. */
export function uyapEngelBayraginiKaydet(kod: UyapEngelHataKodu, metin: string): void {
    if (bayrak === null) {
        bayrak = {kod: kod, metin: metin};
    }
}

export function uyapEngelBayraginiAl(): UyapEngelBayragi | null {
    return bayrak === null ? null : {...bayrak};
}

export function uyapEngelBayraginiTemizle(): void {
    bayrak = null;
}

// ---------------------------------------------------------------- host kancaları (kaydedilmezse özellik KAPALI)

/**
 * Kilit KURULURKEN bir kez çağrılır (bildirim + ses + uyarı penceresi host'ta). Kanca KAYITSIZSA özellik tamamen
 * kapalıdır: tanıma, kapı, kilit, bayrak çalışmaz, eski davranış sürer (eski host + yeni plugin karışık yayını).
 * Host kancası YALNIZ arayüzden sorumludur (durum aynası, uyarı penceresi, bildirim/ses): botu DURDURMAZ (tek sahip
 * plugin'in `uyapEngeliBotuDurdur` action'ı), plugin kilidini kurmaz/kaldırmaz, ağ çağrısı yapmaz. Kanca hata
 * fırlatırsa plugin yutar ve loglar; kilit bozulmaz.
 */
export function uyapEngelKancasiniAyarla(fn: ((bilgi: UyapEngelBilgisi) => void) | null): void {
    kuruldu = fn;
}

/** Kilit herhangi bir yolla kalkınca çağrılır (isteğe bağlı: host kendi uyarı durumunu/kaydını temizlemek için). */
export function uyapEngelKaldirildiKancasiniAyarla(fn: ((kaynak: UyapEngelKaldirmaKaynagi) => void) | null): void {
    kaldirildi = fn;
}

/** Yeni host tanımı: kanca kayıtlı. false ise özellik kapalıdır. */
export function uyapEngelKancasiKayitliMi(): boolean {
    return kuruldu !== null;
}

/** YALNIZ testler: tüm durumu, kancaları ve dış kaynağı sıfırlar. */
export function uyapEngelSifirla(): void {
    kilit = null;
    bayrak = null;
    kuruldu = null;
    kaldirildi = null;
    disKaynak = null;
}
